import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  ProcessingBasisNote,
  ErrorState,
  Input,
  LoadingState,
  Select,
  Toast,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import {
  ApiError,
  endpoints,
  type AvailableTransition,
  type FindingDetail as FindingDetailPayload,
} from '../state/api.js';
import { useSession } from '../state/session.js';
import { PhotoAttachments } from '../components/PhotoAttachments.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import {
  actionState,
  buildTransitionRequest,
  isComplete,
  type ActionValues,
} from '../lib/actions.js';

/**
 * Layar Finding Detail — Figma `9:2` · M02 · rute `/findings/:code`.
 * Izin LDR MGR; OPR hanya temuan miliknya (`OPR_OWN` di manifest).
 *
 * Yang menentukan bilah aksi BUKAN layar ini. Server mengirim
 * `available_transitions` dari `workflow_def` yang DIPATOK pada butir itu,
 * sudah disaring izin, state, dan entitlement. Akibatnya dua janji terpenuhi
 * sekaligus:
 *
 *   · Tiga transisi milik M07 (Escalate, Step down, Take over) HILANG dari bilah
 *     aksi bila M07 tidak ter-entitlement — bukan tampil mati (adendum A-01).
 *   · Guard yang belum terpenuhi mengembalikan `blocked_by`, sehingga tombol
 *     tampil TETAPI mati dengan alasan yang terbaca — mis. "Selesaikan temuan"
 *     mati sampai foto sesudah dilampirkan.
 *
 * `item_event` adalah sumber kebenaran; kolom `state` hanyalah cache-nya (ADR-4),
 * dan riwayat di bawah menampilkan peristiwanya, bukan ringkasannya.
 */
/**
 * Dasar pemrosesan yang DITAMPILKAN layar ini, dan kenapa ia konstanta di sini.
 *
 * Nilai yang mengikat adalah `processing_basis` pada rute
 * `POST /v1/findings/{code}/transitions` di `modules/M02/module.manifest.json` —
 * dari sanalah GUC diisi dan dari sanalah basis data mencap setiap baris riwayat.
 * Yang di bawah ini adalah SALINANNYA untuk ditampilkan, dan batas kejujurannya
 * perlu dinyatakan: tidak ada gerbang yang membandingkan keduanya hari ini.
 *
 * Yang MEMANG dijaga gerbang: setiap nilai di bawah berasal dari kosakata tertutup
 * (`privacy-vocabulary.json`), dan setiap kunci yang dirakit darinya wajib punya
 * kalimatnya di KEDUA bahasa — gerbang teks antarmuka mengenumerasinya dari
 * kosakata itu, bukan dari daftar tulis tangan. Jadi kalimatnya tidak dapat hilang;
 * yang masih dapat menyimpang adalah PILIHAN dasarnya, dan itu tercatat sebagai
 * risiko sisa di CONSENT-DAN-PDP.md.
 */
/**
 * TIDAK ADA KONSTANTA DASAR PEMROSESAN DI BERKAS INI LAGI — telaah kode 6, M-7.
 *
 * Bentuk sebelumnya memaku tiga nilai di sini:
 *
 *     const DASAR_TEMUAN  = 'LEGAL_OBLIGATION';
 *     const PASAL_TEMUAN  = '20(2)c';
 *     const TUJUAN_TEMUAN = ['OHS_COMPLIANCE', 'IMPROVEMENT_TRACKING'];
 *
 * Ketiganya BENAR pada hari ditulis, dan code reviewer memverifikasinya terhadap
 * manifest, terhadap kosakata, dan terhadap baris riwayat yang benar-benar tertulis di
 * basis data. Itu justru yang membuatnya temuan: sebuah salinan yang benar hari ini adalah
 * salinan yang berbeda tiga sprint kemudian, dan yang berbeda di sini akan dibaca pekerja
 * sebagai keterangan dasar hukum atas pemrosesan data tentang dirinya. Tidak ada gerbang
 * yang membandingkan keduanya.
 *
 * Sekarang nilainya datang dari `finding.processing_basis` — dirakit server dari
 * `processing_basis` pada rute `POST /v1/findings/{code}/transitions` di
 * `modules/M02/module.manifest.json`, yaitu sumber yang SAMA dengan GUC
 * `app.processing_basis` yang dicap basis data ke setiap baris riwayat butir ini.
 *
 * Yang menjaganya: `tools/ci/assert-kaki-dasar-pemrosesan.sh` menolak konstanta dasar
 * hukum, pasal, dan tujuan di dalam `apps/console/src/**`, dan
 * `packages/ui/src/telaah6-kaki-dasar-dari-server.test.tsx` merender kaki formulirnya
 * tanpa DOM lalu membandingkan hasilnya dengan manifest.
 *
 * Kalimatnya tetap dirender KLIEN dari katalog dua bahasa (ADR T1-06): server mengirim
 * KODE (`LEGAL_OBLIGATION`, `OHS_COMPLIANCE`) dan pasalnya, tidak pernah prosa.
 */

export function FindingDetail({ code, onBack }: { code: string; onBack: () => void }) {
  const { t, translator, locale } = useSession();
  const [finding, setFinding] = useState<FindingDetailPayload | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<AvailableTransition | null>(null);
  const [values, setValues] = useState<ActionValues>({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);
  /**
   * Daftar orang untuk pemilih penanggung jawab (UAT D-04, aksi "Tugaskan").
   *
   * Dimuat MALAS — hanya ketika dialog yang membutuhkannya dibuka — karena
   * `GET /v1/users` ber-izin MGR dan seorang leader area tidak berhak
   * memanggilnya. Memuatnya bersama layar akan membuat setiap pembukaan Detail
   * Temuan oleh leader menghasilkan satu 403 di log yang bukan gejala apa pun.
   */
  const [people, setPeople] = useState<Array<{ id: string; full_name: string | null }>>([]);
  const [peopleError, setPeopleError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      setFinding(await endpoints.finding(code));
      setStatus('ready');
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
      setStatus('error');
    }
  }, [code]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Membuka dialog sebuah aksi, dan memuat apa yang dialognya butuhkan. */
  const openAction = useCallback(async (transition: AvailableTransition) => {
    setValues({});
    setPeopleError(null);
    setPending(transition);
    if (!(transition.requires_input ?? []).includes('owner_id')) return;
    try {
      const payload = await endpoints.users();
      setPeople(payload.items);
    } catch (err) {
      // Dialog tetap terbuka dan mengatakan apa yang terjadi; ia tidak menjadi
      // pemilih kosong tanpa penjelasan (peta §5.9 "kondisi gagal").
      setPeopleError(err instanceof ApiError ? err.problem.code : 'INTERNAL');
    }
  }, []);

  async function applyTransition(transition: AvailableTransition, isian: ActionValues) {
    setBusy(true);
    try {
      await endpoints.transition(code, buildTransitionRequest(transition, isian));
      setToast({
        kind: 'success',
        text: locale === 'id-ID' ? transition.label_id : transition.label_en,
      });
      setPending(null);
      setValues({});
      await load();
    } catch (err) {
      // Penolakan server dirender dalam bahasa pengguna dari `{code, params}` —
      // server tidak pernah mengirim kalimatnya (ADR T1-06).
      setToast({
        kind: 'danger',
        text: err instanceof ApiError ? renderProblem(translator, err.problem) : t('error.INTERNAL'),
      });
    } finally {
      setBusy(false);
    }
  }

  if (status === 'loading') {
    return (
      <section className="page">
        <LoadingState rows={5} label={t('state.loading.title')} />
      </section>
    );
  }

  if (status === 'error' || !finding) {
    const notFound = error?.problem.code === 'NOT_FOUND';
    return (
      <section className="page">
        <ErrorState
          title={notFound ? t('finding.notFound.title') : t('state.error.title')}
          body={notFound ? t('finding.notFound.body') : t('state.error.body')}
          code={error?.problem.request_id ?? null}
          retryLabel={t('app.retry')}
          onRetry={() => void load()}
        />
      </section>
    );
  }

  return (
    <section className="page">
      <header className="page__head">
        <div>
          <Button variant="text" onClick={onBack}>
            ← {t('findings.title')}
          </Button>
          <h1 className="page__title">
            {finding.code} · {finding.title}
          </h1>
          <p className="page__subtitle">
            <Badge badgeTone={toneOf(finding.state)}>{t(`state.${finding.state}`)}</Badge>{' '}
            {finding.facets.map((f) => (
              <Badge key={f} tone={f === 'OVERDUE' ? 'danger' : 'warning'}>
                {t(`facet.${f}`)}
              </Badge>
            ))}{' '}
            {finding.is_safety ? <Badge tone="danger">{t('category.SAFETY')}</Badge> : null}
          </p>
        </div>
      </header>

      <div className="detail">
        <div className="detail__main">
          <section className="card">
            <dl className="facts">
              <div>
                <dt>{t('finding.reportedBy', { name: '' })}</dt>
                <dd>{finding.reported_by.full_name}</dd>
              </div>
              <div>
                <dt>{t('finding.owner')}</dt>
                <dd>{finding.owner?.full_name ?? t('findings.unassigned')}</dd>
              </div>
              <div>
                <dt>{t('finding.area')}</dt>
                <dd>
                  {finding.location?.code} {finding.location?.name}
                </dd>
              </div>
              <div>
                <dt>{t('finding.category')}</dt>
                <dd>{finding.category_code ? t(`category.${finding.category_code}`) : '—'}</dd>
              </div>
              <div>
                <dt>{t('finding.severity')}</dt>
                <dd>{finding.severity ? t(`severity.${finding.severity}`) : '—'}</dd>
              </div>
              <div>
                <dt>{t('finding.due')}</dt>
                <dd>{formatDate(finding.due_at, locale)}</dd>
              </div>
              <div>
                <dt>{t('finding.opened')}</dt>
                <dd>{formatDateTime(finding.opened_at, locale)}</dd>
              </div>
              <div>
                <dt>{t('finding.closed')}</dt>
                <dd>{formatDateTime(finding.closed_at, locale)}</dd>
              </div>
            </dl>
            {finding.description ? <p className="detail__desc">{finding.description}</p> : null}
          </section>

          {/*
            FOTO — DITAMPILKAN dan DAPAT DIUNGGAH (UAT U2-07 dan LDR-4).

            Bentuk sebelumnya ada DI SINI dan merender `<div className="photo__frame"
            aria-hidden="true" />` — bingkai kosong — beserta `figcaption` yang jatuh ke
            `a.file_object_id`, sehingga yang terbaca di layar adalah ULID mentah. Nol
            elemen `<img>`, nol `input[type=file]`, di seluruh konsol.

            Ia dipindahkan ke komponennya sendiri karena ia bukan lagi tampilan: ia
            memegang jalur unggah tiga langkah (ADR-8) beserta perhitungan checksum dan
            keadaan "sedang diperiksa". Lihat kepala `PhotoAttachments.tsx`.
          */}
          <PhotoAttachments
            code={finding.code ?? code}
            attachments={finding.attachments}
            roles={['BEFORE', 'AFTER']}
            /*
              Siapa yang boleh melampirkan ditentukan SERVER — izin rute `OPR_OWN LDR MGR`
              pada `POST /v1/findings/{code}/attachments`, ditambah RLS. Yang di sini
              hanyalah menyembunyikan tombol dari orang yang pasti ditolak, dan
              petunjuknya diambil dari transisi yang server sendiri nyatakan tersedia:
              sebuah temuan yang sudah CLOSED tidak menerima lampiran baru.
            */
            canAttach={finding.available_transitions.length > 0}
            onChanged={load}
          />

          <section className="card">
            <h2 className="card__title">{t('finding.timeline')}</h2>
            <p className="card__muted">{t('finding.timeline.note')}</p>
            <ol className="timeline">
              {finding.events.map((e) => (
                <li key={e.id}>
                  <span className="timeline__when">{formatDateTime(e.occurred_at, locale)}</span>
                  <span className="timeline__what">
                    {t(`event.${e.event_code}`, {
                      actor: e.actor_name ?? e.actor_kind,
                      code: finding.code ?? '',
                      owner: finding.owner?.full_name ?? '',
                      level: finding.escalation_level,
                    })}
                  </span>
                  {e.from_state && e.to_state && e.from_state !== e.to_state ? (
                    <span className="timeline__states">
                      {t(`state.${e.from_state}`)} → {t(`state.${e.to_state}`)}
                    </span>
                  ) : null}
                  {e.note ? <blockquote className="timeline__note">{e.note}</blockquote> : null}
                </li>
              ))}
            </ol>
          </section>

          <section className="card">
            <h2 className="card__title">{t('finding.comments')}</h2>
            {finding.comments.length === 0 ? (
              <p className="card__muted">{t('finding.comment.empty')}</p>
            ) : (
              <ul className="comments">
                {finding.comments.map((c) => (
                  <li key={c.id}>
                    <Avatar name={c.author_name ?? '?'} size={28} />
                    <div>
                      <strong>{c.author_name}</strong>
                      <small>{formatDateTime(c.created_at, locale)}</small>
                      <p>{c.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Bilah aksi. Isinya datang dari server, bukan dari daftar di layar ini. */}
        <aside className="detail__aside">
          <section className="card">
            <h2 className="card__title">{t('finding.actions')}</h2>
            {finding.available_transitions.length === 0 ? (
              <p className="card__muted">{t('finding.actions.none')}</p>
            ) : (
              <div className="actions">
                {finding.available_transitions.map((tr) => {
                  /**
                   * UAT D-04. Dua pertanyaan, dua jawaban — dan keputusannya ada
                   * di `lib/actions.ts`, bukan di dalam JSX ini:
                   *
                   *   · `blocked_by` → syarat KEADAAN belum terpenuhi; tombolnya
                   *     mati dan mengatakan sebabnya ("Terlampir 0 dari 1");
                   *   · `requires_input` → masukan belum diketik; tombolnya HIDUP
                   *     dan menekannya membuka dialog yang mengumpulkannya.
                   *
                   * Bentuk sebelumnya hanya mengenal yang pertama, sehingga
                   * Tugaskan, Ubah tenggat, dan Kembalikan mati selamanya.
                   */
                  const s = actionState(tr);
                  return (
                    <div key={tr.code} className="actions__row">
                      <Button
                        variant={tr.code === 'CONFIRM_FIX' ? 'primary' : 'secondary'}
                        fullWidth
                        disabled={s.disabled}
                        onClick={() => {
                          if (s.needsDialog) void openAction(tr);
                          else void applyTransition(tr, {});
                        }}
                      >
                        {locale === 'id-ID' ? tr.label_id : tr.label_en}
                      </Button>
                      {/* Tombol yang mati mengatakan MENGAPA ia mati. */}
                      {s.blockedReasonKey ? (
                        <p className="actions__blocked">
                          {t(s.blockedReasonKey, { required: 1, found: 0 })}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            {/*
              BARIS DASAR PEMROSESAN di kaki formulir aksi.
              
              Setiap aksi di panel ini menulis data tentang ORANG LAIN — siapa yang
              ditugaskan, kapan tenggatnya, mengapa dikembalikan. Tidak ada yang
              dapat dicentang di sini: pekerjaannya harus berjalan karena dasarnya
              bukan persetujuan melainkan kewajiban pengelolaan K3 dan pelaksanaan
              hubungan kerja. Yang dapat dilakukan, dan dilakukan, adalah
              MENYATAKAN dasar itu di tempat pengumpulannya.
              
              Dasar yang sama dicap basis data ke setiap baris riwayat butir ini,
              dari GUC yang diisi dari manifest rutenya. Jadi kalimat di bawah dan
              isi `payload.processing_basis` pada baris riwayat berasal dari satu
              sumber, dan tidak dapat berbeda.
            */}
            {/*
              `processing_basis` server yang menentukan isinya. Bila server tidak
              menyatakannya, kaki formulirnya TIDAK dirender: sebuah keterangan dasar hukum
              yang dikarang klien lebih buruk daripada tidak ada keterangan.
            */}
            {finding.processing_basis ? (
            <ProcessingBasisNote
              label={t('privacy.basisNote.label')}
              sentence={t('privacy.basisNote.sentence', {
                basis: t(`privacy.basis.${finding.processing_basis.legal_basis}`),
              })}
              article={finding.processing_basis.article}
              purposes={t('privacy.basisNote.purposes', {
                purposes: finding.processing_basis.purposes
                  .map((p) => t(`privacy.purpose.${p}`))
                  .join(', '),
              })}
              photoGuidance={t('privacy.basisNote.photoGuidance')}
              /*
                Rinciannya tertutup secara bawaan. Yang terlihat di kaki formulir ini
                tinggal satu baris dasar pemrosesan ditambah instruksi foto — dan
                instruksi foto tetap terbuka karena ia instruksi kerja, bukan
                keterangan hukum.
              */
              moreLabel={t('privacy.basisNote.more')}
              noticeLink={
                <a className="link" href="/privacy-notice">
                  {t('privacy.notice.link')}
                </a>
              }
            />
            ) : null}
          </section>
        </aside>
      </div>

      {/* Dialog aksi: medannya DITURUNKAN dari `requires_input` transisinya, jadi
          satu dialog melayani Tugaskan, Ubah tenggat, Kembalikan, dan apa pun yang
          menyusul — tanpa layar ini menyimpan daftar medannya sendiri. */}
      <Dialog
        open={pending !== null}
        kind={pending && actionState(pending).destructive ? 'destructive' : 'confirm'}
        title={pending ? (locale === 'id-ID' ? pending.label_id : pending.label_en) : ''}
        description={
          pending && actionState(pending).fields.some((f) => f.kind === 'reason')
            ? t('finding.reason.required')
            : t('dialog.action.describe')
        }
        confirmLabel={pending ? (locale === 'id-ID' ? pending.label_id : pending.label_en) : ''}
        cancelLabel={t('app.cancel')}
        busy={busy}
        confirmDisabled={pending ? !isComplete(actionState(pending).fields, values) : true}
        onCancel={() => {
          setPending(null);
          setValues({});
        }}
        onConfirm={() => pending && void applyTransition(pending, values)}
      >
        {pending
          ? actionState(pending).fields.map((field) => {
              const value = values[field.name] ?? '';
              const set = (next: string) => setValues((v) => ({ ...v, [field.name]: next }));
              const kosong = value.trim() === '';

              if (field.kind === 'owner') {
                return (
                  <Select
                    key={field.name}
                    label={t(field.labelKey)}
                    placeholder={t('finding.owner.choose')}
                    options={people.map((p) => ({ value: p.id, label: p.full_name ?? p.id }))}
                    value={value}
                    onChange={(e) => set(e.target.value)}
                    error={
                      peopleError
                        ? t(`error.${peopleError}`, { module: 'M01', organisation: '' })
                        : kosong
                          ? t('error.OWNER_REQUIRED')
                          : null
                    }
                  />
                );
              }
              if (field.kind === 'date') {
                return (
                  <Input
                    key={field.name}
                    label={t(field.labelKey)}
                    inputType="date"
                    value={value}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => set(e.target.value)}
                    error={kosong ? t('error.DUE_DATE_IN_PAST') : null}
                  />
                );
              }
              return (
                <Input
                  key={field.name}
                  label={t(field.labelKey)}
                  value={value}
                  onChange={(e) => set(e.target.value)}
                  error={kosong ? t('error.REASON_REQUIRED') : null}
                />
              );
            })
          : null}
      </Dialog>

      {toast ? (
        <div className="assist-toast-stack">
          <Toast
            kind={toast.kind}
            signalWord={toast.kind === 'success' ? t('signal.success') : t('signal.danger')}
            title={toast.text}
            onDismiss={() => setToast(null)}
            closeLabel={t('app.dismiss')}
          />
        </div>
      ) : null}
    </section>
  );
}

function toneOf(state: string): string {
  switch (state) {
    case 'CLOSED':
      return 'SUCCESS';
    case 'AWAITING_VERIFICATION':
    case 'SENT_BACK':
      return 'WARNING';
    default:
      return 'INFORMATION';
  }
}
